import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Auth state — access token kept in memory (Zustand), refresh token in
 * httpOnly cookie set by booqly-api. We only persist a hint that the user
 * was logged in (so we don't show the sign-in screen briefly during refresh).
 */
interface AuthState {
  accessToken: string | null;
  email: string | null;
  setTokens: (accessToken: string, email: string) => void;
  clear: () => void;
  tryRefresh: () => Promise<string | null>;
}

const apiUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8081";

// Shared across every caller (openapi-fetch interceptor + auth-fetch) so a
// burst of concurrent 401s coalesces into a single refresh. See tryRefresh.
let refreshInFlight: Promise<string | null> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      email: null,
      setTokens: (accessToken, email) => set({ accessToken, email }),
      clear: () => set({ accessToken: null, email: null }),
      tryRefresh: async () => {
        // Single-flight: many requests can 401 at once, but the backend rotates
        // and revokes the refresh token on every /auth/refresh call — parallel
        // refreshes would revoke each other and bounce the user to sign-in. Late
        // callers await the one in-flight refresh instead of firing their own.
        if (refreshInFlight) return refreshInFlight;
        refreshInFlight = (async () => {
          try {
            // The refresh token rides along as an httpOnly cookie
            // (credentials:"include"); the backend reads it from the cookie and
            // rotates it back via Set-Cookie. No token in the body.
            const res = await fetch(`${apiUrl}/api/v1/auth/refresh`, {
              method: "POST",
              credentials: "include",
            });
            if (!res.ok) return null;
            const data = (await res.json()) as { accessToken: string };
            set({ accessToken: data.accessToken });
            return data.accessToken;
          } catch {
            return null;
          } finally {
            refreshInFlight = null;
          }
        })();
        return refreshInFlight;
      },
    }),
    {
      // Dev convenience: persist the access token across reloads so a fresh tab
      // doesn't immediately bounce to the auth surface. Production should keep
      // the access token in memory only and rely on the httpOnly refresh cookie.
      name: "booqly-app-auth",
      partialize: (s) => ({ email: s.email, accessToken: s.accessToken }),
    }
  )
);
